"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { getCurrentCustomer, getCurrentUser } from "@/lib/api";
import { fsPageNameFromPath } from "@/lib/fs";

const ORG_ID = process.env.NEXT_PUBLIC_FULLSTORY_ORG_ID?.trim() ?? "";

declare global {
  interface Window {
    FS?: (...args: unknown[]) => unknown;
  }
}

function fsCall(...args: unknown[]) {
  if (typeof window !== "undefined" && typeof window.FS === "function") {
    window.FS(...args);
  }
}

async function identifyCurrentUser() {
  try {
    const [user, customer] = await Promise.all([getCurrentUser(), getCurrentCustomer()]);

    if (user && user.role !== "CUSTOMER") {
      fsCall("setIdentity", {
        uid: user.id,
        properties: {
          displayName: user.name || user.email,
          email: user.email,
          role: user.role,
          businessId: user.businessId ?? undefined,
          businessName: user.business?.name,
          businessSlug: user.business?.slug,
          businessType: user.business?.businessType,
          isDemo: user.business?.isDemo ?? false,
          onboardingComplete: user.business?.onboardingComplete ?? false,
        },
      });
      return;
    }

    if (customer) {
      fsCall("setIdentity", {
        uid: `customer:${customer.id}`,
        properties: {
          displayName: customer.name || customer.email,
          email: customer.email,
          role: "CUSTOMER",
        },
      });
      return;
    }

    fsCall("setIdentity", { anonymous: true });
  } catch {
    // Session still records anonymously if /me fails.
  }
}

/**
 * Official FullStory snippet (same as Settings → Fullstory Setup).
 * Org ID must be in NEXT_PUBLIC_FULLSTORY_ORG_ID at **web build** time.
 * Capture only runs after analytics cookie consent.
 */
export function FullStoryAnalytics() {
  const { consent, ready } = useCookieConsent();
  const pathname = usePathname();
  const analyticsOk = ready && !!consent?.analytics;

  useEffect(() => {
    if (!ORG_ID || !ready) return;

    try {
      fsCall("setProperties", {
        type: "page",
        properties: {
          pageName: fsPageNameFromPath(pathname),
          pagePath: pathname || "/",
        },
      });

      if (analyticsOk) {
        fsCall("setIdentity", { consent: true });
        fsCall("restart");
        void identifyCurrentUser();
      } else {
        fsCall("setIdentity", { consent: false, anonymous: true });
        fsCall("shutdown");
      }
    } catch (err) {
      console.warn("[FullStory] consent/capture update failed", err);
    }
  }, [analyticsOk, ready, pathname]);

  if (!ORG_ID) {
    return null;
  }

  // Exact snippet shape from FullStory (org id last arg). Loads on every page so their
  // install checker can see fs.js; we shutdown until analytics consent.
  const snippet = `!function(m,n,e,t,l,o,g,y){var u,a,f=function(h){
return!(h in m)||(m.console&&m.console.log&&m.console.log("FullStory namespace conflict. Please use a different namespace."),!1)}(l)
;f&&(g=m[l]=function(){var b=function(b,d,j,r){r=r||2;var i,c=/Async$/;return c.test(b)&&(b=b.replace(c,""),
"function"==typeof Promise)?new Promise((function(i,c){h(b,d,j,i,c,r)})):h(b,d,j,i,i,r)};function h(h,d,j,r,i,c){
return b._api?b._api(h,d,j,r,i,c):(b.q&&b.q.push([h,d,j,r,i,c]),null)}return b.q=[],b}(),y=function(b){function h(h){
"function"==typeof h[4]&&h[4](new Error(b))}var d=g.q;if(d){for(var j=0;j<d.length;j++)h(d[j]);d.length=0,d.push=h}},function(){
var b="script",d=n.createElement(b);d.async=!0,d.crossOrigin="anonymous",d.src="https://"+t+"?org="+o,d.setAttribute("data-fs-namespace",l),
d.onerror=function(){y("Error loading "+t)};var c=n.getElementsByTagName(b)[0]
;c&&c.parentNode?c.parentNode.insertBefore(d,c):n.head.appendChild(d)}(),function(){function b(){}function h(b,h,d){g(b,h,d,1)}function d(b,d,j){
h("setProperties",{type:b,properties:d},j)}function j(b,h){d("user",b,h)}function r(b,h,d){j({uid:b},d),h&&j(h,d)}g.identify=r,g.setUserVars=j,
g.identifyAccount=b,g.clearUserCookie=b,g.setVars=d,g.event=function(b,d,j){h("trackEvent",{name:b,properties:d},j)},g.anonymize=function(){r(!1)
},g.shutdown=function(){h("shutdown")},g.restart=function(){h("restart")},g.log=function(b,d){h("log",{level:b,msg:d})},g.consent=function(b){
h("setIdentity",{consent:!arguments.length||b})}}(),u="fetch",a="XMLHttpRequest",g._w={},g._w[a]=m[a],g._w[u]=m[u],m[u]&&(m[u]=function(){
return g._w[u].apply(this,arguments)}),g("init",{env:{orgId:o,host:e,script:t}}),g._v="2.1.0")
}(window,document,"fullstory.com","edge.fullstory.com/s/fs.js","FS",${JSON.stringify(ORG_ID)});
window.FS&&window.FS("setIdentity",{consent:false});
window.FS&&window.FS("shutdown");`;

  return (
    <Script id="fullstory-snippet" strategy="afterInteractive">
      {snippet}
    </Script>
  );
}
